import { useEffect, useState, useSyncExternalStore } from "react";
import { getApps, initializeApp } from "firebase/app";
import {
  initializeAuth,
  inMemoryPersistence,
  verifyPasswordResetCode,
  validatePassword,
  confirmPasswordReset,
} from "firebase/auth";
import { CENTRAL_FIREBASE_CONFIG } from "./firebase-config";
import {
  ACCOUNT_LOGIN_URL,
  parseAccountAction,
  passwordCriteria,
  PasswordActionController,
} from "./account-action";
import { AccountSuccess } from "./AccountSuccess";
import "./account-action.css";

function goToSignIn() {
  window.location.replace(ACCOUNT_LOGIN_URL);
}

function PasswordForm({
  controller,
}: {
  controller: PasswordActionController;
}) {
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
  );
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    void controller.load();
  }, [controller]);
  if (state.view === "success")
    return <AccountSuccess kind="password" onContinue={goToSignIn} />;
  return (
    <main className="account-action-page" dir="rtl" lang="ar">
      <section
        className="account-action-card"
        aria-labelledby="password-action-title"
      >
        <img
          className="account-action-brand"
          src="/email/institutional-header-v1.png"
          alt="جامعة طيبة | وادي طيبة"
        />
        <p className="account-action-muted">
          لوحة المتابعة التنفيذية لمؤتمرات جامعة طيبة
        </p>
        <h1 id="password-action-title">إعداد كلمة المرور</h1>
        {state.view === "loading" ? (
          <p role="status">جارٍ التحقق من الرابط وتحميل متطلبات كلمة المرور…</p>
        ) : state.view === "invalid" ? (
          <>
            <p role="alert">
              تعذر التحقق من رابط إعداد كلمة المرور. قد يكون الرابط منتهي
              الصلاحية أو مستخدمًا، أو تعذر الاتصال. أعد المحاولة أو اطلب رابطًا
              جديدًا من مسؤول النظام.
            </p>
            <a className="button button--primary" href={ACCOUNT_LOGIN_URL}>
              الانتقال إلى تسجيل الدخول
            </a>
          </>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void controller.save();
            }}
          >
            <p className="account-action-muted">
              اختر كلمة مرور خاصة بك تستوفي المتطلبات أدناه.
            </p>
            <label htmlFor="new-password">كلمة المرور الجديدة</label>
            <div className="account-password-field">
              <input
                id="new-password"
                name="new-password"
                type={visible ? "text" : "password"}
                autoComplete="new-password"
                dir="ltr"
                required
                disabled={state.busy}
                aria-describedby="password-policy"
                onChange={(event) =>
                  void controller.setPassword(event.target.value)
                }
              />
              <button
                type="button"
                className="account-password-toggle"
                aria-pressed={visible}
                onClick={() => setVisible(!visible)}
              >
                {visible ? "إخفاء" : "إظهار"}
              </button>
            </div>
            <div
              id="password-policy"
              className="password-policy"
              aria-live="polite"
              aria-atomic="true"
            >
              <p>متطلبات كلمة المرور الحالية</p>
              {state.validation ? (
                <ul>
                  {passwordCriteria(state.validation).map((criterion) => (
                    <li
                      key={criterion.key}
                      className={criterion.met ? "is-met" : undefined}
                    >
                      <span aria-hidden="true">
                        {criterion.met ? "✓" : "○"}
                      </span>
                      <span>
                        <span className="visually-hidden">
                          {criterion.met ? "مستوفى: " : "غير مستوفى: "}
                        </span>
                        <bdi>{criterion.label}</bdi>
                        {criterion.sample ? (
                          <>
                            {" "}
                            (<bdi dir="ltr">{criterion.sample}</bdi>)
                          </>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>جارٍ التحقق من المتطلبات…</p>
              )}
            </div>
            <label htmlFor="confirm-password">تأكيد كلمة المرور</label>
            <input
              id="confirm-password"
              name="confirm-password"
              type={visible ? "text" : "password"}
              autoComplete="new-password"
              dir="ltr"
              required
              disabled={state.busy}
              aria-describedby="password-match"
              onChange={(event) =>
                controller.setConfirmation(event.target.value)
              }
            />
            <p
              id="password-match"
              className="account-action-muted"
              role="status"
            >
              {state.hasConfirmation
                ? state.matches
                  ? "✓ كلمتا المرور متطابقتان"
                  : "كلمتا المرور غير متطابقتين بعد"
                : "أعد إدخال كلمة المرور للتأكيد."}
            </p>
            {state.error ? (
              <p role="alert">
                {state.error === "policy"
                  ? "تعذر التحقق من السياسة. تحقق من الاتصال وأعد إدخال كلمة المرور."
                  : "لم تُحفظ كلمة المرور. تحقق من المتطلبات واتصالك، واطلب رابطًا جديدًا إذا انتهت صلاحية الرابط."}
              </p>
            ) : null}
            <button
              className="button button--primary"
              type="submit"
              disabled={
                state.busy ||
                state.validating ||
                !state.validation?.isValid ||
                !state.matches
              }
            >
              {state.busy ? "جارٍ الحفظ…" : "حفظ كلمة المرور"}
            </button>
          </form>
        )}
        <p className="account-action-footer">وادي طيبة</p>
      </section>
    </main>
  );
}

export function createPasswordActionPage(search: string) {
  const action = parseAccountAction(search);
  if (action.kind === "builtin") {
    const target = new URL(action.url);
    target.searchParams.set("apiKey", CENTRAL_FIREBASE_CONFIG.apiKey!);
    window.location.replace(target.href);
    return (
      <p dir="rtl" role="status">
        جارٍ الانتقال الآمن إلى خدمة الحساب…
      </p>
    );
  }
  // Remove the one-time code from browser history before rendering any content.
  window.history.replaceState(null, "", "/account/action");
  const app =
    getApps().find((entry) => entry.name === "account-action") ??
    initializeApp(CENTRAL_FIREBASE_CONFIG, "account-action");
  const auth = initializeAuth(app, { persistence: inMemoryPersistence });
  auth.languageCode = "ar";
  const controller = new PasswordActionController(
    action.kind === "password" ? action.code : "",
    {
      async verify(code) {
        if (!code) throw new Error("invalid-action");
        await verifyPasswordResetCode(auth, code);
      },
      validate: (password) => validatePassword(auth, password),
      confirm: (code, password) => confirmPasswordReset(auth, code, password),
    },
  );
  return <PasswordForm controller={controller} />;
}
